import styles from './cardPrice.scss';
import cn from 'classnames';
import { Price as PriceModel } from 'models/Price';
import { priceFormat } from 'components/Shared/Price/priceFormat';
import { NO_ELEMENT, rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { useSelector } from 'react-redux';
import { PageState } from 'models/PageState';
import { isProductPage } from 'models/ProductPageModel';
import { getFormattedPrice, type Page } from 'components/Shared/Price/themes/BGH/getFormattedPrice';
import { getIsRentalProduct } from '../../selector';
import { selectBlackFridayStatus } from 'components/Header/themes/BGH/selector';
import { getChannel } from 'views/ProductPage/selector';
import { ToolTipDialog } from 'components/Shared/ToolTip/themes/BGH-RD3';
import { Show } from '@bygghemmait/components';

interface PriceContainerProps {
  children: React.ReactNode;
  isCampaign?: boolean;
  noPadding?: boolean;
  blackFriday?: boolean;
  channel?: string;
}

const PriceContainer = ({ children, isCampaign, blackFriday, channel }: PriceContainerProps) => {
  return (
    <div
      className={cn(styles.priceContainer, {
        [styles.campaign]: isCampaign,
        [styles.BNOBlackFriday]: channel === 'BNO' && blackFriday
      })}
    >
      {children}
    </div>
  );
};

interface FromProps extends Page {
  isFromPrice?: boolean;
  isFamily?: boolean;
}

const From = ({ isFromPrice, page, isFamily }: FromProps) => {
  if (!isFromPrice) {
    return NO_ELEMENT;
  }

  return (
    <span
      className={cn(styles.from, {
        [styles.familyOldPrice]: isFamily && (page === 'productpage' || page === 'productlist')
      })}
    >
      {rm('pl_product_price_from')}
    </span>
  );
};

interface PresentationPriceProps extends Page {
  price?: number;
  className?: string;
  isFromPrice?: boolean;
  quantity?: string;
  unit?: string;
  inCard?: boolean;
  shouldHide?: boolean;
  part?: {
    partPrice?: number;
    partPriceUnit?: string;
  };
  hiddenPrice?: boolean;
  isFamily?: boolean;
  isBundleSavings?: boolean;
}

export const PriceText = ({
  price,
  className,
  unit,
  isFromPrice,
  inCard,
  part,
  shouldHide,
  hiddenPrice,
  page,
  isFamily,
  isBundleSavings
}: PresentationPriceProps) => {
  if (price === undefined || shouldHide) {
    return NO_ELEMENT;
  }

  const formattedPrice = priceFormat({ price });
  const qaDataAttr = inCard ? {} : getQaDataAttr({ price: price });
  const shouldShowBundleTooltip = !inCard && isBundleSavings;

  return hiddenPrice ? (
    <span className={className}>{rm('pl_product_non_purchable')}</span>
  ) : (
    <span className={className}>
      <From isFromPrice={isFromPrice} page={page} isFamily={isFamily} />
      <span {...qaDataAttr}>{formattedPrice}</span>
      <Unit part={part} unit={unit} inCard={inCard} />
      <Show when={shouldShowBundleTooltip}>
        <span className={styles.bundleTooltipInformation}>
          <ToolTipDialog text={rm('product_bundle_savings_information')} />
        </span>
      </Show>
    </span>
  );
};

interface UnitProps {
  unit?: string;
  inCard?: boolean;
  part?: {
    partPrice?: number;
    partPriceUnit?: string;
  };
}

const Unit = ({ unit = `&nbsp;${rm('checkout_unit')}`, inCard, part }: UnitProps) => {
  if (!unit) {
    return NO_ELEMENT;
  }

  const _unit = part?.partPrice ? `&nbsp;${rm('checkout_unit')}` : unit;
  const quantityUnit = inCard && part?.partPrice ? part?.partPriceUnit : undefined;

  return (
    <>
      <span dangerouslySetInnerHTML={{ __html: _unit || rm('checkout_unit') }} />
      {!!quantityUnit && <span dangerouslySetInnerHTML={{ __html: `/${quantityUnit}` }} />}
    </>
  );
};

interface PricePerUnitProps {
  salesUnit?: string;
  price?: number;
  part?: {
    partPrice?: number;
    partPriceUnit?: string;
  };
}

const getHasPricePerUnit = ({ part, price = 0, salesUnit }: PricePerUnitProps) => {
  const hasSalesUnit = price && salesUnit;
  const hasPartPrice = part?.partPrice && part?.partPriceUnit;
  return Boolean(hasPartPrice) || Boolean(hasSalesUnit);
};
const PricePerUnit = ({ part, price = 0, salesUnit }: PricePerUnitProps) => {
  const hasPricePerUnit = getHasPricePerUnit({ part, price, salesUnit });
  const hasSalesUnit = price && salesUnit;

  if (!hasPricePerUnit) {
    return NO_ELEMENT;
  }

  if (hasSalesUnit) {
    return (
      <span className={styles.pricePerUnit}>
        {rm('price_per_unit_st', salesUnit, priceFormat({ price }))}
      </span>
    );
  }

  return (
    <span className={styles.pricePerUnit}>
      {priceFormat({ price: part?.partPrice || 0 })}
      {` ${rm('checkout_unit')}${part?.partPriceUnit || ''}`}
    </span>
  );
};

interface LowestPossiblePriceProps {
  lowestPossiblePrice?: number;
  priceUnit?: string;
  hasMinimumQuantityRequirement?: boolean;
}

const getHasLowestPossiblePrice = ({
  lowestPossiblePrice = 0,
  priceUnit,
  hasMinimumQuantityRequirement
}: LowestPossiblePriceProps) => {
  return lowestPossiblePrice && priceUnit && hasMinimumQuantityRequirement;
};
const LowestPossiblePrice = ({
  lowestPossiblePrice = 0,
  priceUnit,
  hasMinimumQuantityRequirement
}: LowestPossiblePriceProps) => {
  const hasLowestPossiblePrice = getHasLowestPossiblePrice({
    lowestPossiblePrice,
    priceUnit,
    hasMinimumQuantityRequirement
  });

  if (!hasLowestPossiblePrice) {
    return NO_ELEMENT;
  }

  return (
    <span className={styles.lowestPossiblePrice}>
      {`${rm('lowest_price')} ${priceFormat({ price: lowestPossiblePrice })}${rm(
        'pl_product_price_unit'
      )}`}
    </span>
  );
};

interface PriceSectionProps extends Page {
  price: Optional<PriceModel>;
  isCampaign?: boolean;
  isPartialCampaign?: boolean;
  isBundleSavings?: boolean;
  isLowPrice?: boolean;
  isLoweredPrice?: boolean;
  quantity?: string;
  salesUnit?: string;
  salesQuantity?: number;
  inCard?: boolean;
  hasMinimumQuantityRequirement?: boolean;
  part?: {
    partPrice?: number;
    partPriceUnit?: string;
  };
  noPadding?: boolean;
  isFamily?: boolean;
  blackFriday?: boolean;
  channel?: string;
}

const selectLowestQuantity = ({ currentPage }: PageState) => {
  return isProductPage(currentPage) ? currentPage.product.quantity.lowest : 1;
};

type GetShouldDisplayRow = {
  part: PriceSectionProps['part'];
  pricePerUnit?: number;
  salesUnit?: string;
  hasMinimumQuantityRequirement?: boolean;
  lowestPossiblePrice?: number;
  currencyUnit?: string;
  formattedPrice: number;
  isPartialCampaign?: boolean;
};
const getShouldDisplayRow = ({
  part,
  pricePerUnit,
  salesUnit,
  hasMinimumQuantityRequirement,
  lowestPossiblePrice,
  currencyUnit,
  formattedPrice,
  isPartialCampaign
}: GetShouldDisplayRow) => {
  const hasPricePerUnit = getHasPricePerUnit({ part, price: pricePerUnit, salesUnit });
  const hasRecommendedPrice = !!formattedPrice;
  const hasLowestPossiblePrice = getHasLowestPossiblePrice({
    hasMinimumQuantityRequirement,
    lowestPossiblePrice,
    priceUnit: currencyUnit
  });
  return (
    Boolean(hasPricePerUnit || hasRecommendedPrice || hasLowestPossiblePrice) && !isPartialCampaign
  );
};

const PriceSection = ({
  price,
  isCampaign,
  isPartialCampaign,
  isLowPrice,
  isLoweredPrice,
  isBundleSavings,
  quantity,
  inCard,
  part,
  hasMinimumQuantityRequirement,
  salesUnit,
  page = 'productlist',
  noPadding,
  isFamily,
  blackFriday,
  channel
}: PriceSectionProps) => {
  const { lowestPossiblePrice, presentationPrice, currencyUnit, isFromPrice, hiddenPrice } = price;

  const { price: formattedPrice } = getFormattedPrice({
    page,
    isCampaign,
    isLowPrice,
    isLoweredPrice,
    price
  });

  const lowestQuantity = useSelector(selectLowestQuantity);

  const pricePerUnit =
    lowestPossiblePrice && lowestQuantity
      ? lowestPossiblePrice / lowestQuantity
      : lowestPossiblePrice;

  const shouldDisplayRow = getShouldDisplayRow({
    part,
    pricePerUnit,
    salesUnit,
    formattedPrice,
    hasMinimumQuantityRequirement,
    lowestPossiblePrice,
    currencyUnit,
    isPartialCampaign
  });

  return (
    <PriceContainer
      isCampaign={isCampaign && !hiddenPrice}
      noPadding={noPadding}
      blackFriday={blackFriday}
      channel={channel}
    >
      <PriceText
        inCard={inCard}
        className={styles.price}
        price={presentationPrice}
        unit={currencyUnit}
        part={part}
        isFromPrice={isFromPrice}
        quantity={quantity}
        hiddenPrice={hiddenPrice}
      />
      {shouldDisplayRow && !hiddenPrice && (
        <span
          className={cn(styles.row, {
            [styles.campaign]: isCampaign
          })}
        >
          <PriceText
            className={cn(styles.recommendedPrice, {
              [styles.campaign]: isCampaign
            })}
            inCard={inCard}
            price={formattedPrice}
            unit={currencyUnit}
            shouldHide={!formattedPrice}
            hiddenPrice={hiddenPrice}
            isFromPrice={isFamily}
            page={page}
            isFamily={isFamily}
            isBundleSavings={isBundleSavings}
          />
          <PricePerUnit part={part} price={pricePerUnit} salesUnit={salesUnit} />
          <LowestPossiblePrice
            hasMinimumQuantityRequirement={hasMinimumQuantityRequirement}
            lowestPossiblePrice={price.lowestPossiblePrice}
            priceUnit={currencyUnit}
          />
        </span>
      )}
    </PriceContainer>
  );
};

interface CampaignContainerProps {
  children: React.ReactNode;
  twoColumns?: boolean;
  isCampaign: boolean;
  blackFriday?: boolean;
  channel?: string;
}

const CampaignContainer = ({
  children,
  isCampaign,
  twoColumns = false,
  blackFriday,
  channel
}: CampaignContainerProps) => {
  return (
    <div
      className={cn(styles.campaignContainer, {
        [styles.campaign]: isCampaign,
        [styles.twoColumns]: twoColumns,
        [styles.BNOBlackFriday]: channel === 'BNO' && blackFriday
      })}
    >
      {children}
    </div>
  );
};

interface CampaignSectionProps {
  splashTextEnd?: string;
  splashTextTop?: string;
  isCampaign: boolean;
  isLowPrice: boolean;
  isLoweredPrice: boolean;
  isBundleSavings: boolean;
  hasCampaignStyle?: boolean;
  page?: PriceProps['page'];
  hiddenPrice: boolean;
  isFamily?: boolean;
  blackFriday?: boolean;
  channel?: string;
}

const getText = ({
  isLowPrice,
  isCampaign,
  isLoweredPrice,
  isBundleSavings
}: Pick<
  CampaignSectionProps,
  'isCampaign' | 'isLowPrice' | 'isLoweredPrice' | 'isBundleSavings'
>): string => {
  if (isBundleSavings) {
    return rm('product_bundle_savings_text');
  }

  if (isLoweredPrice) {
    return rm('product_non_campaign_lowered');
  }

  if (isLowPrice) {
    return rm('product_non_campaign');
  }

  if (isCampaign) {
    return rm('product_campaign');
  }

  return '';
};

interface SplashTextProps {
  splashText?: string;
  oneText: boolean;
}

const getSplashTextComponent = ({ splashText, oneText }: SplashTextProps) => {
  if (!splashText) return NO_ELEMENT;

  return (
    <p
      className={cn(styles.splashTextEnd, {
        [styles.oneSplashText]: oneText
      })}
    >
      {splashText}
    </p>
  );
};

const CampaignSection = ({
  splashTextEnd,
  splashTextTop,
  isCampaign,
  isBundleSavings,
  isLowPrice,
  isLoweredPrice,
  hasCampaignStyle = false,
  page,
  hiddenPrice,
  isFamily,
  blackFriday,
  channel
}: CampaignSectionProps) => {
  const text = getText({ isLowPrice, isCampaign, isLoweredPrice, isBundleSavings });
  const hasSplash = Boolean(splashTextTop || text || splashTextEnd);
  if (!hasSplash || hiddenPrice) {
    return NO_ELEMENT;
  }

  const oneSplashText =
    Boolean(splashTextTop && !splashTextEnd) || Boolean(!splashTextTop && splashTextEnd);

  const switchVariants = ['switchProductlist'] as (typeof page)[];
  if (switchVariants.includes(page)) {
    return (
      <CampaignContainer
        isCampaign={isCampaign || hasCampaignStyle}
        blackFriday={blackFriday}
        channel={channel}
      >
        {(!isCampaign || !isFamily) && (
          <p
            className={cn(styles.splashTextMiddle, {
              [styles.BNOBlackFriday]: channel === 'BNO' && blackFriday
            })}
          >
            {text}
          </p>
        )}
        <div style={{ textAlign: 'right' }}>
          {splashTextTop && splashTextEnd ? (
            <>
              <p className={styles.splashTextEnd}>{splashTextTop}</p>
              <p className={styles.splashTextEnd}>{splashTextEnd}</p>
            </>
          ) : (
            getSplashTextComponent({ splashText: splashTextTop || splashTextEnd, oneText: true })
          )}
        </div>
        {isCampaign && isFamily && (
          <p
            className={cn(styles.splashTextMiddle, {
              [styles.BNOBlackFriday]: channel === 'BNO' && blackFriday
            })}
          >
            {text}
          </p>
        )}
      </CampaignContainer>
    );
  }

  return (
    <CampaignContainer
      isCampaign={isCampaign || hasCampaignStyle}
      blackFriday={blackFriday}
      channel={channel}
    >
      {getSplashTextComponent({ splashText: splashTextTop, oneText: oneSplashText })}
      <p
        className={cn(styles.splashTextMiddle, {
          [styles.BNOBlackFriday]: channel === 'BNO' && blackFriday
        })}
      >
        {text}
      </p>
      {getSplashTextComponent({ splashText: splashTextEnd, oneText: oneSplashText })}
    </CampaignContainer>
  );
};

type Optional<T> = { [P in keyof T]?: T[P] };

export interface PriceProps extends Page {
  isCampaign?: boolean;
  isPartialCampaign?: boolean;
  isLowPrice?: boolean;
  isLoweredPrice?: boolean;
  isBundleSavings?: boolean;
  price: PriceModel;
  splashTextMiddle?: string;
  splashTextEnd?: string;
  splashTextTop?: string;
  quantityUnit?: string;
  salesUnit?: string;
  salesQuantity?: number;
  hasMinimumQuantityRequirement?: boolean;
  partPrice?: number;
  partPriceUnit?: string;
  inCard?: boolean;
  hasCampaignStyle?: boolean;
  noPadding?: boolean;
  isFamily?: boolean;
}

const getPriceOrRent = (price: PriceModel): PriceModel => {
  const isRentalProduct = getIsRentalProduct(price);
  return {
    ...price,
    presentationPrice: isRentalProduct ? price.rentalPrice : price.presentationPrice
  };
};

export const CardPrice = ({
  page = 'productlist',
  isCampaign = false,
  isPartialCampaign = false,
  isLowPrice = false,
  isLoweredPrice = false,
  isBundleSavings = false,
  hasCampaignStyle = false,
  splashTextEnd = '',
  splashTextTop = '',
  price,
  quantityUnit,
  salesUnit,
  salesQuantity,
  inCard,
  partPrice,
  partPriceUnit,
  hasMinimumQuantityRequirement,
  noPadding,
  isFamily
}: PriceProps) => {
  const _price = getPriceOrRent(price);
  const blackFridayIsActive = useSelector(selectBlackFridayStatus);
  const channel = useSelector(getChannel);

  return (
    <div
      className={cn(styles[page], {
        [styles.campaign]: !_price.hiddenPrice && (isCampaign || hasCampaignStyle),
        [styles.BNOBlackFriday]: channel === 'BNO' && blackFridayIsActive
      })}
    >
      <PriceSection
        page={page}
        part={{ partPrice, partPriceUnit }}
        price={_price}
        isCampaign={isCampaign}
        isPartialCampaign={isPartialCampaign}
        isBundleSavings={isBundleSavings}
        isLowPrice={isLowPrice}
        isLoweredPrice={isLoweredPrice}
        quantity={quantityUnit}
        salesUnit={salesUnit}
        salesQuantity={salesQuantity}
        inCard={inCard}
        hasMinimumQuantityRequirement={hasMinimumQuantityRequirement}
        noPadding={noPadding}
        isFamily={isFamily}
        blackFriday={blackFridayIsActive}
        channel={channel}
      />
      <CampaignSection
        page={page}
        hasCampaignStyle={hasCampaignStyle}
        splashTextEnd={splashTextEnd}
        splashTextTop={splashTextTop}
        isCampaign={isCampaign}
        isLowPrice={isLowPrice}
        isBundleSavings={isBundleSavings}
        isLoweredPrice={isLoweredPrice}
        hiddenPrice={_price.hiddenPrice}
        isFamily={isFamily}
        blackFriday={blackFridayIsActive}
        channel={channel}
      />
    </div>
  );
};
